/**
 * Page-local URL params for ClientOptOutsPage's three filter pieces of state that were NOT
 * surviving back-navigation or a refresh: the reason filter, the channel filter, and the
 * show-inactive toggle. Second adoption of the pattern #78 established on TaxReturnsPage
 * (useTaxReturnsScopeParams.ts) -- proving it transfers to a page with no shared-codec usage at
 * all, rather than inventing a new convention.
 *
 * Deliberately NOT part of the shared `useListSearchParams` codec, for the same reason #78's
 * hook isn't: `reason`/`channel`/`showInactive` are this page's own scope, not generic
 * list-filter grammar, and a fetch scope folded into the shared codec's `setFilters` would sit in
 * the blast radius of "any filter change clears every other filter.* param wholesale".
 *
 * Conventions (mirroring useTaxReturnsScopeParams.ts exactly):
 * - `reason=<value>` is present only when set; an unrecognised value falls back to unset (never
 *   throws, never leaks a bad enum value into state).
 * - `channel=<value>` same rule, scoped to the four real OptOutChannel values.
 * - `inactive=1` is present ONLY when true, omitted when false. Same stable-binary-default
 *   caveat as #78's hook: this cannot distinguish "explicitly false" from "unset", which is
 *   fine here and would be WRONG for a tri-state field -- do not copy this onto one.
 * - Changing any of them clears `page` (back to 1), matching the shared codec's page hygiene.
 * - Mutations replace history (never push), matching the shared codec.
 * - A pristine view (no reason, no channel, inactive=false) produces a URL with NONE of them.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { OptOutReason, OptOutChannel } from '../types';

const VALID_REASONS: readonly OptOutReason[] = [
  'not_filing_this_year', 'using_different_preparer', 'doing_own_taxes',
  'deceased', 'moved_away', 'unsubscribe_request', 'other',
];
const VALID_CHANNELS: readonly OptOutChannel[] = ['email', 'sms', 'both', 'none'];

export interface ClientOptOutsScopeParams {
  reason: OptOutReason | undefined;
  channel: OptOutChannel | undefined;
  inactive: boolean;
  setReason: (reason: OptOutReason | undefined) => void;
  setChannel: (channel: OptOutChannel | undefined) => void;
  setInactive: (inactive: boolean) => void;
}

export function useClientOptOutsScopeParams(): ClientOptOutsScopeParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const reason = useMemo<OptOutReason | undefined>(() => {
    const raw = searchParams.get('reason');
    return raw && (VALID_REASONS as string[]).includes(raw) ? (raw as OptOutReason) : undefined;
  }, [searchParams]);

  const channel = useMemo<OptOutChannel | undefined>(() => {
    const raw = searchParams.get('channel');
    return raw && (VALID_CHANNELS as string[]).includes(raw) ? (raw as OptOutChannel) : undefined;
  }, [searchParams]);

  const inactive = searchParams.get('inactive') === '1';

  /** Replace history (never push), via the functional form — safe against a stale closure. */
  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setReason = useCallback(
    (next: OptOutReason | undefined) => {
      update((params) => {
        if (next === undefined) params.delete('reason');
        else params.set('reason', next);
        params.delete('page');
      });
    },
    [update]
  );

  const setChannel = useCallback(
    (next: OptOutChannel | undefined) => {
      update((params) => {
        if (next === undefined) params.delete('channel');
        else params.set('channel', next);
        params.delete('page');
      });
    },
    [update]
  );

  const setInactive = useCallback(
    (next: boolean) => {
      update((params) => {
        if (next) params.set('inactive', '1');
        else params.delete('inactive');
        params.delete('page');
      });
    },
    [update]
  );

  return { reason, channel, inactive, setReason, setChannel, setInactive };
}
